/**
 * useLogout Hook
 *
 * Hook for handling user logout with proper cleanup and navigation.
 * Calls Clerk's signOut() directly to terminate the session, then navigates.
 *
 * Previously this hook only called the platform-api /auth/logout endpoint via
 * the SDK, which never terminated the Clerk session — leaving the user
 * perpetually "signed in" on the client.
 */

import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useErrorCapture } from '@tonnie/observability';
import { useWebPush } from '@tonnie/sdk';
import { useCallback, useState } from 'react';
import { useLocation } from 'wouter';

/**
 * Hook return type
 */
interface IUseLogoutReturn {
  /** Trigger logout */
  logout: () => void;
  /** Whether logout is in progress */
  isLoggingOut: boolean;
}

/**
 * Hook options
 */
interface IUseLogoutOptions {
  /** Path to redirect after logout */
  redirectTo?: string;
  /** Callback after successful logout */
  onSuccess?: () => void;
  /** Callback on logout error */
  onError?: (error: Error) => void;
}

/**
 * Hook for handling user logout
 *
 * @param options - Configuration options
 * @returns Logout function and loading state
 *
 * @example
 * ```tsx
 * const { logout, isLoggingOut } = useLogout({ redirectTo: '/login' });
 *
 * return (
 *   <button onClick={logout} disabled={isLoggingOut}>
 *     {isLoggingOut ? 'Logging out...' : 'Logout'}
 *   </button>
 * );
 * ```
 */
export function useLogout(options: IUseLogoutOptions = {}): IUseLogoutReturn {
  const { redirectTo = '/login', onSuccess, onError } = options;
  const [, setLocation] = useLocation();
  const { signOut: clerkSignOut } = useClerkAuth();
  const { captureException } = useErrorCapture();
  const { unsubscribe: unsubscribeWebPush } = useWebPush(
    import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined
  );

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);

    // Clear every Tonnie-scoped storage key before Clerk signs out, so
    // stale tenant/journey/notification data from the previous identity
    // doesn't leak into the next user who signs in on the same browser.
    try {
      const TONNIE_PREFIX = 'tonnie_';
      for (const storage of [sessionStorage, localStorage]) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key && key.startsWith(TONNIE_PREFIX)) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => storage.removeItem(k));
      }
    } catch {
      // Storage exceptions (quota, privacy mode) should never block logout
    }

    // D10: unregister this browser's push subscription WHILE still
    // authenticated — the server-side unregister call needs the session
    // that's about to go away. `unsubscribeWebPush` already swallows its own
    // errors internally, but the try/catch here holds the contract at THIS
    // call site too: sign-out must complete no matter what the SDK does.
    try {
      await unsubscribeWebPush();
    } catch {
      // A push failure should never block sign-out.
    }

    try {
      await clerkSignOut();
      onSuccess?.();
    } catch (error) {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        component: 'useLogout',
      });
      onError?.(error as Error);
    } finally {
      setIsLoggingOut(false);
    }

    setLocation(redirectTo);
  }, [
    clerkSignOut,
    setLocation,
    redirectTo,
    onSuccess,
    onError,
    captureException,
    unsubscribeWebPush,
  ]);

  return {
    logout,
    isLoggingOut,
  };
}

export default useLogout;
